/**
 * useScopedNavigate — drop-in `useNavigate` replacement that prefixes
 * the destination with the current user's scope.
 *
 * For super_admin who has entered a reseller AND a customer:
 *   navigate('/account') → /reseller/1/customer/1001/account
 *
 * For reseller_owner with a customer entered:
 *   navigate('/account') → /customer/1001/account
 *
 * For customer roles (or when no scope is entered):
 *   navigate('/account') → /account
 *
 * Pass `{ ignoreScope: true }` to opt out (e.g. when navigating to
 * `/customers` which is intentionally cross-tenant).
 */
import { useNavigate, useLocation } from 'react-router-dom';
import { buildScopedPath, isCustomerScopedPath } from '@/lib/scopedUrl';
import { useCustomerContext } from '@/lib/customer-context-store';
import { useResellerContext } from '@/lib/reseller-context-store';
import { useAuth } from '@/lib/auth-store';

export function useScopedNavigate() {
  const navigate = useNavigate();
  const customer = useCustomerContext((s) => s.current);
  const reseller = useResellerContext((s) => s.current);
  const user = useAuth((s) => s.user);

  return (to: string, opts?: { replace?: boolean; ignoreScope?: boolean }) => {
    if (opts?.ignoreScope || !isCustomerScopedPath(to)) {
      return navigate(to, { replace: opts?.replace });
    }
    const isSuper = Boolean(user?.is_super_admin);
    const scope: { resellerNumber?: number; customerNumber?: number } = {};
    if (customer?.customerNumber != null) scope.customerNumber = customer.customerNumber;
    if (isSuper && reseller?.resellerNumber != null) scope.resellerNumber = reseller.resellerNumber;
    return navigate(buildScopedPath(to, scope), { replace: opts?.replace });
  };
}

/** Pure helper variant — useful when you have the user + context already. */
export function scopedHref(
  to: string,
  ctx: {
    isSuperAdmin: boolean;
    customerNumber: number | null;
    resellerNumber: number | null;
  },
): string {
  if (!isCustomerScopedPath(to)) return to;
  const scope: { resellerNumber?: number; customerNumber?: number } = {};
  if (ctx.customerNumber != null) scope.customerNumber = ctx.customerNumber;
  if (ctx.isSuperAdmin && ctx.resellerNumber != null) scope.resellerNumber = ctx.resellerNumber;
  return buildScopedPath(to, scope);
}

/**
 * Read the current URL path stripped of any scope prefix. Used by the
 * sidebar to highlight the right nav item regardless of which URL
 * variant the user is on.
 *
 *   '/reseller/1/customer/1001/recordings' → '/recordings'
 *   '/customer/1001/recordings'             → '/recordings'
 *   '/recordings'                           → '/recordings'
 */
export function useTailPath(): string {
  const { pathname } = useLocation();
  const m = /^(?:\/reseller\/\d+)?(?:\/customer\/\d+)?(\/.*)?$/.exec(pathname);
  return m?.[1] || '/';
}
